import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import contactService from '../services/contactService';
import groupManagementService from '../services/groupManagementService';
import botService from '../services/botService';
import Modal from '../components/Modal';
import { useNavigate } from 'react-router-dom';
import useAlert from '../hooks/useAlert';
import { useTranslation } from 'react-i18next';
import './Contacts.css';

const Contacts = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [botId, setBotId] = useState(null);
  const [bots, setBots] = useState([]);
  const [memberStatuses, setMemberStatuses] = useState({});
  const [managingMember, setManagingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalState, setModalState] = useState({
    details: { isOpen: false, contact: null },
    verify: { isOpen: false, result: null },
    addGroup: { isOpen: false, contact: null },
    removeGroup: { isOpen: false, contact: null },
    block: { isOpen: false, contact: null },
  });

  const { alert, DialogComponent: AlertDialog } = useAlert();

  const openModal = (modal, data) => {
    setModalState((prev) => ({ ...prev, [modal]: { isOpen: true, ...data } }));
  };

  const closeModal = (modal) => {
    setModalState((prev) => ({ ...prev, [modal]: { isOpen: false, [Object.keys(prev[modal])[1]]: null } }));
  };

  useEffect(() => {
    const fetchBots = async () => {
      try {
        const data = await botService.getAllBots();
        setBots(data);
      } catch (err) {
        console.error('Erro ao carregar bots:', err);
      }
    };

    fetchBots();
  }, []);

  useEffect(() => {
    const selectedBotId = localStorage.getItem('selectedBotId');
    if (selectedBotId) {
      setBotId(selectedBotId);
    } else {
      setLoading(false);
      setError(t('common.no_bot_selected', 'Nenhum bot selecionado. Por favor, selecione um bot primeiro.'));
    }
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await contactService.getAllContacts(botId);
      setContacts(data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar contatos');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (botId) {
      fetchContacts();
    }
  }, [botId]);

  const handleSync = async () => {
    if (!botId) {
      alert(t('common.select_bot_first', 'Por favor, selecione um bot primeiro.'), t('common.attention', 'Atenção'), 'info');
      return;
    }
    setSyncing(true);
    try {
      const result = await contactService.syncGroupAdmins(botId);
      await alert(result.message || 'Sincronização concluída.', 'Sucesso', 'success');
      fetchContacts(); // Reload contacts
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao sincronizar.', 'Erro', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const checkMemberStatus = async (contactId) => {
    try {
      const status = await groupManagementService.checkMemberStatus(botId, contactId);
      openModal('verify', { result: status });
    } catch (err) {
      console.error('Erro ao verificar status do membro:', err);
    }
  };

  const handleManageMember = async (contact, action) => {
    openModal(action === 'add' ? 'addGroup' : 'removeGroup', { contact });
  };

  const handleBlock = async (contact) => {
    openModal('block', { contact });
  };

  const handleShowDetails = (contact) => {
    openModal('details', { contact });
  };
  
  const confirmAddGroup = async (contact) => {
    setManagingMember(contact.id);
    try {
      await groupManagementService.addMember(botId, contact.id);
      checkMemberStatus(contact.id);
    } catch (err) {
      console.error(`Erro ao adicionar membro:`, err);
    } finally {
      setManagingMember(null);
      closeModal('addGroup');
    }
  };
  
  const confirmRemoveGroup = async (contact) => {
    setManagingMember(contact.id);
    try {
      await groupManagementService.removeMember(botId, contact.id);
      checkMemberStatus(contact.id);
    } catch (err) {
      console.error(`Erro ao remover membro:`, err);
    }
    finally {
      setManagingMember(null);
      closeModal('removeGroup');
    }
  };
  
  const confirmBlock = async (contact) => {
    try {
      await contactService.blockContact(contact.id, botId);
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contact.id ? { ...c, is_blocked: !c.is_blocked } : c
        )
      );
    } catch (err) {
      console.error('Erro ao bloquear/desbloquear contato:', err);
    } finally {
      closeModal('block');
    }
  };

  const handleBotFilterChange = (e) => {
    const selectedBotId = e.target.value;
    localStorage.setItem('selectedBotId', selectedBotId);
    setBotId(selectedBotId);
  };
  
  const filteredContacts = contacts.filter((contact) => {
    if (!searchTerm) {
      return true;
    }
    return (contact.name && contact.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (contact.username && contact.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (contact.telegram_id && contact.telegram_id.toString().includes(searchTerm));
  });

  const sortedContacts = React.useMemo(() => {
    let sortableItems = [...filteredContacts];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredContacts, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedContacts.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <Layout>
      <AlertDialog />
      <div className="contacts-page">
        <div className="contacts-header">
          <h1>{t('contacts.title', 'Contatos')}</h1>
          <div className="contacts-filters">
            <input
              type="text"
              placeholder={t('contacts.search_placeholder', 'Pesquisar por nome, username ou ID do Telegram...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select onChange={handleBotFilterChange} value={botId || ''}>
              <option value="" disabled>{t('contacts.select_bot', 'Selecione um bot')}</option>
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
            </select>
            <button onClick={handleSync} className="btn btn-secondary" disabled={syncing}>
              {syncing ? t('contacts.syncing', 'Sincronizando...') : t('contacts.sync_admins', 'Sincronizar Admins')}
            </button>
          </div>
        </div>
        {loading && <p>{t('common.loading', 'Carregando...')}</p>}
        {error && <p className="error">{error}</p>}
        {!loading && !error && (
          <div className="contacts-table-container">
            <table className="contacts-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('name')}>{t('contacts.name', 'Nome')}</th>
                  <th onClick={() => requestSort('username')}>{t('contacts.username', 'Username')}</th>
                  <th onClick={() => requestSort('phone')}>{t('contacts.phone', 'Telefone')}</th>
                  <th onClick={() => requestSort('email')}>{t('contacts.email', 'Email')}</th>
                  <th>{t('common.actions', 'Ações')}</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((contact) => (
                  <tr key={contact.id}>
                    <td>{contact.name}</td>
                    <td>{contact.username}</td>
                    <td>{contact.phone}</td>
                    <td>{contact.email}</td>
                    <td>
                      <button
                        onClick={() => handleShowDetails(contact)}
                        className="btn btn-info-100 text-primary-600 radius-8 px-14 py-6 text-sm"
                        title={t('contacts.details', 'Detalhes')}
                      >
                        {t('contacts.details', 'Detalhes')}
                      </button>
                      {memberStatuses[contact.id] && (
                        <span className={`member-status-badge ${memberStatuses[contact.id].is_member ? 'member' : 'not-member'}`}>
                          {memberStatuses[contact.id].is_member ? '✓ No grupo' : '✗ Fora'}
                        </span>
                      )}
                      {!contact.is_bot && (
                        <>
                          {managingMember === contact.id ? (
                            <span className="loading-spinner">...</span>
                          ) : (
                            <>
                              <button
                                onClick={() => checkMemberStatus(contact.id)}
                                className="btn btn-primary-100 text-primary-600  radius-8 px-14 py-6 text-sm"
                                title={t('contacts.verify_status', 'Verificar status no grupo')}
                                disabled={!botId}
                              >
                                {t('contacts.verify', 'Verificar')}
                              </button>
                              <button
                                onClick={() => handleManageMember(contact, 'add')}
                                className="btn btn-success-100 text-success-600 radius-8 px-14 py-6 text-sm"
                                title={t('contacts.add_to_group', 'Adicionar ao grupo')}
                                disabled={!botId}
                              >
                                {t('contacts.add_group', '+ Grupo')}
                              </button>
                              <button
                                onClick={() => handleManageMember(contact, 'remove')}
                                className="btn btn-warning-100 text-warning-600 radius-8 px-14 py-6 text-sm"
                                title={t('contacts.remove_from_group', 'Remover do grupo')}
                                disabled={!botId}
                              >
                                {t('contacts.remove_group', '- Grupo')}
                              </button>
                              <button
                                onClick={() => handleBlock(contact)}
                                className="btn btn-danger-100 text-danger-600 radius-8 px-14 py-6 text-sm"
                                title={t('contacts.block', 'Bloquear')}
                              >
                                {t('contacts.block', 'Bloquear')}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              {Array.from({ length: Math.ceil(sortedContacts.length / itemsPerPage) }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => paginate(i + 1)}
                  className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <Modal
          isOpen={modalState.details.isOpen}
          onClose={() => closeModal('details')}
          title={t('contacts.contact_details', 'Detalhes do Contato')}
        >
          {modalState.details.contact && (
            <div>
              <p><strong>{t('contacts.name', 'Nome')}:</strong> {modalState.details.contact.name}</p>
              <p><strong>{t('contacts.username', 'Username')}:</strong> {modalState.details.contact.username}</p>
              <p><strong>{t('contacts.phone', 'Telefone')}:</strong> {modalState.details.contact.phone}</p>
              <p><strong>{t('contacts.email', 'Email')}:</strong> {modalState.details.contact.email}</p>
              <p><strong>{t('contacts.language', 'Idioma')}:</strong> {modalState.details.contact.language}</p>
              <p><strong>{t('contacts.created_at', 'Criado em')}:</strong> {new Date(modalState.details.contact.created_at).toLocaleString()}</p>
              <p><strong>{t('contacts.updated_at', 'Atualizado em')}:</strong> {new Date(modalState.details.contact.updated_at).toLocaleString()}</p>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={modalState.verify.isOpen}
          onClose={() => closeModal('verify')}
          title={t('contacts.verification_result', 'Resultado da Verificação')}
        >
          {modalState.verify.result && (
            <p>
              {modalState.verify.result.is_member ? t('contacts.is_member', 'O contato é membro do grupo.') : t('contacts.is_not_member', 'O contato não é membro do grupo.')}
            </p>
          )}
        </Modal>

        <Modal
          isOpen={modalState.addGroup.isOpen}
          onClose={() => closeModal('addGroup')}
          title={t('contacts.add_to_group', 'Adicionar ao Grupo')}
        >
          {modalState.addGroup.contact && (
            <div>
              <p>{t('contacts.confirm_add_to_group', {defaultValue: 'Tem certeza que deseja adicionar {{name}} ao grupo?', name: modalState.addGroup.contact.name})}</p>
              <button onClick={() => confirmAddGroup(modalState.addGroup.contact)} className="btn btn-success">{t('common.yes', 'Sim')}</button>
              <button onClick={() => closeModal('addGroup')} className="btn btn-secondary">{t('common.no', 'Não')}</button>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={modalState.removeGroup.isOpen}
          onClose={() => closeModal('removeGroup')}
          title={t('contacts.remove_from_group', 'Remover do Grupo')}
        >
          {modalState.removeGroup.contact && (
            <div>
              <p>{t('contacts.confirm_remove_from_group', {defaultValue: 'Tem certeza que deseja remover {{name}} do grupo?', name: modalState.removeGroup.contact.name})}</p>
              <button onClick={() => confirmRemoveGroup(modalState.removeGroup.contact)} className="btn btn-danger">{t('common.yes', 'Sim')}</button>
              <button onClick={() => closeModal('removeGroup')} className="btn btn-secondary">{t('common.no', 'Não')}</button>
            </div>
          )}
        </Modal>
        
        <Modal
          isOpen={modalState.block.isOpen}
          onClose={() => closeModal('block')}
          title={t('contacts.block_contact', 'Bloquear Contato')}
        >
          {modalState.block.contact && (
            <div>
              <p>{modalState.block.contact.is_blocked
                ? t('contacts.confirm_unblock', {defaultValue: 'Tem certeza que deseja desbloquear {{name}}?', name: modalState.block.contact.name})
                : t('contacts.confirm_block', {defaultValue: 'Tem certeza que deseja bloquear {{name}}?', name: modalState.block.contact.name})}</p>
              <button onClick={() => confirmBlock(modalState.block.contact)} className="btn btn-danger">{t('common.yes', 'Sim')}</button>
              <button onClick={() => closeModal('block')} className="btn btn-secondary">{t('common.no', 'Não')}</button>
            </div>
          )}
        </Modal>

      </div>
    </Layout>
  );
};

export default Contacts;
